import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, getSelf } from '../services/client';
import { BilingualText } from '../components/BilingualText';
import { BrandHeader } from '../components/BrandHeader';
import { dict } from '../i18n/dictionary';

export function ChangePin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isInitial = params.get('initial') === '1';
  const self = getSelf();
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<{ ja: string; vi: string } | null>(null);
  const [done, setDone] = useState(false);

  const PIN_RE = /^\d{4,8}$/;

  const onSubmit = async (): Promise<void> => {
    if (!PIN_RE.test(currentPin) || !PIN_RE.test(newPin)) {
      setErrorMsg(dict.pinInvalid);
      return;
    }
    if (newPin !== confirmPin) {
      setErrorMsg(dict.pinMismatch);
      return;
    }
    if (currentPin === newPin) {
      setErrorMsg(dict.pinSameAsCurrent);
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await api.changeMyPin(currentPin, newPin);
      setDone(true);
      setTimeout(() => navigate('/punch'), 1500);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body?.error;
        if (body?.code === 'INVALID_PIN') {
          setErrorMsg(dict.pinWrong);
        } else if (body?.code === 'SAME_PIN') {
          setErrorMsg(dict.pinSameAsCurrent);
        } else {
          setErrorMsg({
            ja: body?.message_ja ?? 'エラー',
            vi: body?.message_vi ?? 'Lỗi',
          });
        }
      } else {
        setErrorMsg(dict.networkError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="page page-pin">
        <BrandHeader />
        <div className="check-mark" aria-hidden>
          ✓
        </div>
        <h2>
          <BilingualText value={dict.pinChanged} />
        </h2>
      </main>
    );
  }

  return (
    <main className="page page-pin">
      <BrandHeader />
      <header className="page-header">
        {self && (
          <div className="employee-display">
            <div className="name-ja">{self.name_ja}</div>
            {self.name_vi && <div className="name-vi">{self.name_vi}</div>}
          </div>
        )}
        <h2>
          <BilingualText value={isInitial ? dict.initialChangePinTitle : dict.changePinTitle} />
        </h2>
      </header>

      <div className="pin-form">
        <label className="pin-field">
          <span>
            <BilingualText value={dict.currentPinLabel} />
          </span>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            maxLength={8}
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
            disabled={submitting}
          />
        </label>
        <label className="pin-field">
          <span>
            <BilingualText value={dict.newPinLabel} />
          </span>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
            disabled={submitting}
          />
        </label>
        <label className="pin-field">
          <span>
            <BilingualText value={dict.confirmPinLabel} />
          </span>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={8}
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
            disabled={submitting}
          />
        </label>

        {errorMsg && (
          <p className="error">
            <BilingualText value={errorMsg} />
          </p>
        )}

        <button
          type="button"
          className="btn-primary btn-large"
          onClick={onSubmit}
          disabled={submitting || !currentPin || !newPin || !confirmPin}
        >
          <BilingualText value={dict.changePinSubmit} />
        </button>

        {!isInitial && (
          <button
            type="button"
            className="link-back"
            onClick={() => navigate('/punch')}
            disabled={submitting}
            style={{ marginTop: 16 }}
          >
            <BilingualText value={dict.back} separator="slash" />
          </button>
        )}
      </div>
    </main>
  );
}
